import React, { useEffect, useRef, useState } from "react";
import { STYLES, styleFor } from "./registry";

/**
 * One line in the posting form that says which style the post will use. Tapping
 * it pops up a small menu with a drawn preview of every template (Sam,
 * 2026-09-15: "the small menu that will pop up to help people preview what each
 * templated style will look like"). Pick one and the menu closes.
 *
 * Only opacity and transform animate, so the menu never jumps or stalls.
 */
function StylePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const trigger = useRef(null);
  const menu = useRef(null);
  const current = styleFor(value);

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => setShown(true));
    const selected = menu.current?.querySelector('[aria-checked="true"]');
    if (selected) selected.focus();

    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); close(true); }
    };
    const onClick = (e) => {
      if (!menu.current?.contains(e.target) && !trigger.current?.contains(e.target)) close(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  function close(returnFocus) {
    setShown(false);
    setOpen(false);
    if (returnFocus && trigger.current) trigger.current.focus();
  }

  function choose(key) {
    onChange(key);
    close(true);
  }

  function onArrow(e, index) {
    const columns = window.matchMedia && window.matchMedia("(max-width: 460px)").matches ? 2 : 3;
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = menu.current?.querySelectorAll('[role="radio"]')[index + step];
    if (next) next.focus();
  }

  const Preview = current.Preview;
  return (
    <div className="relative">
      <button
        ref={trigger}
        type="button"
        onClick={() => (open ? close(true) : setOpen(true))}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Style: ${current.label}`}
        className="inline-flex items-center gap-[9px] rounded-full border border-dupontGold/70 bg-dupontCream dark:bg-neutral-800
                   py-[5px] pl-[6px] pr-[12px] font-blairBody text-[13px] font-semibold text-dupontBlue dark:text-darkText
                   hover:bg-dupontCreamDk dark:hover:bg-neutral-700 transition-colors"
      >
        <span className="block w-[34px] overflow-hidden rounded-[4px]" aria-hidden="true"><Preview /></span>
        <span aria-hidden="true">{current.label}</span>
        <span aria-hidden="true" className={"text-dupontGoldDk transition-transform duration-200 motion-reduce:transition-none " + (open ? "rotate-180" : "")}>⌄</span>
      </button>

      {open && (
        <div
          ref={menu}
          // On a phone the menu rises from the bottom edge instead, so it can never
          // run off the side of the screen.
          className={"absolute left-0 top-[calc(100%+8px)] z-30 w-[min(460px,calc(100vw-64px))] rounded-[14px] border border-dupontGoldLt/40 " +
                     "max-[520px]:fixed max-[520px]:inset-x-[12px] max-[520px]:top-auto max-[520px]:bottom-[12px] max-[520px]:w-auto max-[520px]:origin-bottom " +
                     // Never taller than the screen: it scrolls instead, so every style can be reached.
                     "max-[520px]:max-h-[calc(100dvh-24px)] max-[520px]:overflow-y-auto max-[520px]:overscroll-contain " +
                     "bg-white dark:bg-neutral-900 p-[12px] shadow-[0_24px_48px_-18px_rgba(20,16,4,0.35)] origin-top-left " +
                     "transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none " +
                     (shown ? "opacity-100 translate-y-0 scale-100" : "opacity-0 -translate-y-[6px] scale-[0.985]")}
        >
          <div className="flex flex-wrap items-baseline gap-x-[10px] px-[4px] pb-[10px]">
            <span className="font-display font-semibold text-[15px] text-dupontBlue dark:text-darkText">Pick a style</span>
            <span className="font-blairBody text-[12px] text-gray-500">You can change it any time before sharing</span>
          </div>
          <div role="radiogroup" aria-label="Pick a style" className="grid grid-cols-3 max-[460px]:grid-cols-2 gap-[8px]">
            {STYLES.map((style, i) => {
              const Tile = style.Preview;
              const checked = style.key === current.key;
              return (
                <button
                  key={style.key}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  onClick={() => choose(style.key)}
                  onKeyDown={(e) => onArrow(e, i)}
                  className={"grid gap-[1px] rounded-[10px] border p-[6px] pb-[8px] text-left transition-colors " +
                             (checked
                               ? "border-dupontGold bg-dupontCream dark:bg-neutral-800 shadow-[inset_0_0_0_1px_#b5a03e]"
                               : "border-transparent hover:bg-dupontCream dark:hover:bg-neutral-800")}
                >
                  <span className="mb-[5px] block"><Tile /></span>
                  <b className="font-blairBody text-[12px] font-semibold leading-[1.25] text-dupontBlue dark:text-darkText">{style.label}</b>
                  <small className="font-blairBody text-[12px] leading-[1.3] text-gray-500">{style.blurb}</small>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default StylePicker;
